import { useMemo, useState } from "react";
import { Plus, X } from "lucide-react";

import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { useTranslation } from "@/shared/i18n";

import type { CategoryItem, TemplateCategory } from "../api";
import { useCategories, useAddTemplateCategory, useRemoveTemplateCategory } from "../model";

interface Props {
  templateId: string;
  categories: TemplateCategory[];
}

export function CategoriesPanel({ templateId, categories }: Props) {
  const { t } = useTranslation("psdTemplates");
  const { categories: all, isLoading } = useCategories();
  const addMutation = useAddTemplateCategory();
  const removeMutation = useRemoveTemplateCategory();
  const [selectedId, setSelectedId] = useState<string>("");

  const associatedIds = useMemo(
    () => new Set(categories.map((c) => c.categoryId)),
    [categories],
  );

  const available = useMemo(
    () => all.filter((c) => !associatedIds.has(c.id)),
    [all, associatedIds],
  );

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.categoryName.localeCompare(b.categoryName)),
    [categories],
  );

  const handleAdd = async () => {
    if (!selectedId) return;
    try {
      await addMutation.mutateAsync({ templateId, categoryId: selectedId });
      setSelectedId("");
    } catch {
      // toast handled inside hook
    }
  };

  const handleRemove = (categoryId: string) => {
    removeMutation.mutate({ templateId, categoryId });
  };

  const isEmpty = sortedCategories.length === 0;

  return (
    <div
      className={`space-y-2 p-3 rounded-xl border bg-card/40 ${
        isEmpty ? "border-destructive/60" : ""
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">
          {t("categories.title")} <span className="text-destructive">*</span>
        </h3>
        <span className="text-[11px] text-muted-foreground">
          {t("categories.hint")}
        </span>
      </div>

      <div className="flex flex-wrap gap-1.5 min-h-[28px]">
        {isEmpty ? (
          <span className="text-xs text-destructive italic">
            {t("categories.emptyRequired")}
          </span>
        ) : (
          sortedCategories.map((c) => (
            <Badge
              key={c.categoryId}
              variant="secondary"
              className="gap-1 pr-1"
            >
              {c.categoryName}
              <button
                type="button"
                onClick={() => handleRemove(c.categoryId)}
                disabled={removeMutation.isPending}
                aria-label={t("categories.remove", { name: c.categoryName })}
                className="rounded-full hover:bg-background/50 p-0.5 disabled:opacity-50"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))
        )}
      </div>

      <div className="flex gap-2 pt-1">
        <Select value={selectedId} onValueChange={setSelectedId}>
          <SelectTrigger className="h-9 flex-1">
            <SelectValue
              placeholder={
                isLoading
                  ? t("categories.loading")
                  : available.length === 0
                    ? t("categories.allAdded")
                    : t("categories.selectPlaceholder")
              }
            />
          </SelectTrigger>
          <SelectContent>
            {available.map((c: CategoryItem) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          size="sm"
          onClick={handleAdd}
          disabled={!selectedId || addMutation.isPending}
        >
          <Plus className="w-4 h-4 mr-1" />
          {t("categories.add")}
        </Button>
      </div>
    </div>
  );
}
